"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  type DragEvent as ReactDragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type Folder,
  type JobSummary,
  ApiError,
  createFolder as apiCreateFolder,
  deleteFolder as apiDeleteFolder,
  deleteJob,
  listMyJobs,
  updateFolder as apiUpdateFolder,
  updateJobMetadata,
} from "../../lib/api";
import { clearAuth, readToken } from "../../lib/auth";
import {
  loadFolders,
  notifyFoldersChanged,
  useFolders,
} from "../../lib/folders-store";

type EditTarget =
  | { kind: "folder"; id: string; name: string }
  | { kind: "job"; id: string; name: string };

type MoveTarget =
  | { kind: "folder"; id: string; name: string }
  | { kind: "job"; id: string; name: string };

type ConfirmTarget =
  | { kind: "folder"; id: string; name: string; childCount: number }
  | { kind: "job"; id: string; name: string };

interface DragPayload {
  kind: "folder" | "job";
  id: string;
}

const DRAG_MIME = "application/x-snapsite-item";

type ViewMode = "grid" | "list";
// Running was removed: audits typically complete in seconds-to-minutes,
// the chip was disabled (count = 0) almost all the time, and the
// per-card status badge already signals in-progress runs. Keeping only
// the filters that meaningfully prune the list.
// "capture" = Couldn't capture (finished but every page came back
// blank, credits refunded). A distinct outcome the user acts on
// (upload a screenshot), so it gets its own chip — and Completed
// excludes it so the two chips partition finished runs instead of
// double-counting.
type StatusFilter = "all" | "completed" | "failed" | "capture";
type SortKey = "recent" | "name" | "modified";
type TypeFilter = "all" | "audit" | "snapshot" | "imageAudit";

export function ProjectsClient({ folderId }: { folderId: string | null }) {
  const router = useRouter();
  const { folders } = useFolders();
  const [jobs, setJobs] = useState<JobSummary[] | null>(null);
  const [error, setError] = useState<string>("");

  // Refresh job list whenever the folder context changes — and once on
  // mount. We pull all jobs for the user (200 cap matches the backend
  // default) and filter client-side; cheaper than re-querying for each
  // folder navigation and lets us show counts on folder cards too.
  const refreshJobs = useCallback(async () => {
    const token = readToken();
    if (!token) {
      setJobs([]);
      return;
    }
    try {
      const list = await listMyJobs(token, 200);
      setJobs(list);
      setError("");
    } catch (err) {
      setJobs([]);
      if (err instanceof ApiError && err.status === 401) {
        clearAuth();
        return;
      }
      setError(err instanceof Error ? err.message : "Could not load audits.");
    }
  }, []);

  useEffect(() => {
    void refreshJobs();
  }, [refreshJobs]);

  // The folder we're currently inside (null = root).
  const current = useMemo(
    () => (folderId ? folders.find((f) => f.id === folderId) ?? null : null),
    [folderId, folders]
  );
  const isRoot = folderId === null;

  // Bail out gracefully if the folderId in the URL doesn't exist or is
  // trashed (e.g. the user followed a stale link).
  useEffect(() => {
    if (!isRoot && folders.length > 0 && !current) {
      router.replace("/projects");
    }
  }, [isRoot, folders, current, router]);

  // Visible folders + audits in this directory.
  const childFolders = useMemo(
    () =>
      folders
        .filter((f) => !f.trashedAt && (f.parentId ?? null) === folderId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [folders, folderId]
  );
  const childJobs = useMemo(() => {
    const all = jobs ?? [];
    return all
      .filter((j) => !j.trashedAt && (j.folderId ?? null) === folderId)
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [jobs, folderId]);

  // For folder thumbnails / counts: how many items are in each child
  // folder (one level deep — quick scan, not a full descendant count).
  // Returns the split because "4 items" doesn't tell you whether you're
  // walking into 4 audits or 3 folders + 1 audit — the breakdown is
  // more useful at a glance.
  const folderItemCount = useCallback(
    (id: string) => {
      const subfolders = folders.filter(
        (f) => !f.trashedAt && f.parentId === id
      ).length;
      const audits = (jobs ?? []).filter(
        (j) => !j.trashedAt && j.folderId === id
      ).length;
      return { subfolders, audits, total: subfolders + audits };
    },
    [folders, jobs]
  );

  // Breadcrumb ancestry chain — resolved from the in-memory folder list.
  const breadcrumbs = useMemo(() => {
    const trail: Folder[] = [];
    let cursor = current;
    while (cursor) {
      trail.unshift(cursor);
      cursor = folders.find((f) => f.id === cursor!.parentId) ?? null;
    }
    return trail;
  }, [current, folders]);

  // Toolbar state. List is the default view — it scans better for the
  // common "find my audit" task; grid stays one click away.
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<SortKey>("recent");
  // Type filter rendered as a quiet dropdown (like Sort) and only when
  // the list actually mixes types — zero added chrome otherwise.
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  // Apply search + status filter + sort to the child lists.
  const filteredFolders = useMemo(() => {
    let list = childFolders;
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((f) => f.name.toLowerCase().includes(q));
    // Folders have no "status", so status filter only reduces the audit list.
    if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "modified") list = [...list].sort((a, b) => b.updatedAt - a.updatedAt);
    // "recent" keeps the default (alphabetical by name for folders).
    return list;
  }, [childFolders, search, sort]);

  const filteredJobs = useMemo(() => {
    let list = childJobs;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((j) => {
        const name = jobDisplayName(j).toLowerCase();
        const url = j.sourceUrl.toLowerCase();
        return name.includes(q) || url.includes(q);
      });
    }
    if (statusFilter === "completed") {
      list = list.filter((j) => j.status === "done" && !j.captureFailed);
    } else if (statusFilter === "failed") {
      list = list.filter((j) => j.status === "error");
    } else if (statusFilter === "capture") {
      list = list.filter((j) => j.status === "done" && j.captureFailed);
    }
    if (typeFilter !== "all") {
      list = list.filter((j) => (j.runMode ?? "audit") === typeFilter);
    }
    if (sort === "name") {
      list = [...list].sort((a, b) => jobDisplayName(a).localeCompare(jobDisplayName(b)));
    } else if (sort === "modified") {
      list = [...list].sort((a, b) => b.updatedAt - a.updatedAt);
    }
    // "recent" = default sort (by createdAt desc, already set in childJobs).
    return list;
  }, [childJobs, search, statusFilter, typeFilter, sort]);

  // Counts for the status filter chips.
  const completedCount = useMemo(
    () => childJobs.filter((j) => j.status === "done" && !j.captureFailed).length,
    [childJobs]
  );
  const failedCount = useMemo(
    () => childJobs.filter((j) => j.status === "error").length,
    [childJobs]
  );
  const captureCount = useMemo(
    () => childJobs.filter((j) => j.status === "done" && j.captureFailed).length,
    [childJobs]
  );

  // Distinct run modes present in this folder — the Type dropdown only
  // renders when there's genuinely more than one type to filter.
  const presentTypes = useMemo(
    () => new Set(childJobs.map((j) => j.runMode ?? "audit")),
    [childJobs]
  );

  // Modal / dialog state.
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const [confirm, setConfirm] = useState<ConfirmTarget | null>(null);
  // Drag-over state for the folder/breadcrumb drop targets. The three
  // values mean distinct things and a previous version conflated null
  // with "no drag in progress", which made the root "Files" breadcrumb
  // pill render as a permanent drop target whenever the user was
  // inside any subfolder:
  //   "IDLE"  — no drag currently happening (initial / post-drop)
  //   null    — drag is over the root breadcrumb pill
  //   string  — drag is over the folder with that id
  // The literal "BREAD" is kept as a transient "left a breadcrumb but
  // haven't landed on another target yet" state used by the breadcrumb
  // drag-leave handler.
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null | "BREAD" | "IDLE">("IDLE");
  // Items mid-flight on a move/rename so we can dim the row and show a
  // spinner instead of leaving the user wondering why nothing happened.
  const [movingIds, setMovingIds] = useState<Set<string>>(new Set());
  const setMoving = useCallback((id: string, on: boolean) => {
    setMovingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // ---------- mutations ----------

  const handleCreateFolder = useCallback(
    async (name: string) => {
      const token = readToken();
      if (!token) return;
      try {
        await apiCreateFolder(token, { name, parentId: folderId });
        notifyFoldersChanged();
        setCreateFolderOpen(false);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not create folder.";
        setError(msg);
      }
    },
    [folderId]
  );

  const handleRename = useCallback(
    async (kind: "folder" | "job", id: string, name: string) => {
      const token = readToken();
      if (!token) return;
      try {
        if (kind === "folder") {
          await apiUpdateFolder(token, id, { name });
          notifyFoldersChanged();
        } else {
          await updateJobMetadata(token, id, { displayName: name });
          await refreshJobs();
        }
        setEditing(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not rename.");
      }
    },
    [refreshJobs]
  );

  const handleMove = useCallback(
    async (
      kind: "folder" | "job",
      id: string,
      newParentId: string | null
    ) => {
      const token = readToken();
      if (!token) return;
      // No-op on a self-target (same folder) — saves the round trip
      // and avoids a confusing flash.
      if (kind === "folder") {
        const f = folders.find((x) => x.id === id);
        if (f && (f.parentId ?? null) === newParentId) return;
      } else {
        const j = (jobs ?? []).find((x) => x.id === id);
        if (j && (j.folderId ?? null) === newParentId) return;
      }
      setMoving(id, true);
      try {
        if (kind === "folder") {
          await apiUpdateFolder(token, id, { parentId: newParentId });
          notifyFoldersChanged();
        } else {
          await updateJobMetadata(token, id, { folderId: newParentId });
          await refreshJobs();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not move.");
      } finally {
        setMoving(id, false);
      }
    },
    [folders, jobs, refreshJobs, setMoving]
  );

  const handleTrash = useCallback(
    async (kind: "folder" | "job", id: string) => {
      const token = readToken();
      if (!token) return;
      try {
        if (kind === "folder") {
          await apiDeleteFolder(token, id);
          notifyFoldersChanged();
          // Folder cascade also trashed any audits inside; refresh the job list.
          await refreshJobs();
        } else {
          await deleteJob(token, id);
          await refreshJobs();
        }
        setConfirm(null);
      } catch (err) {
        if (err instanceof ApiError) setError(err.message);
        else setError(err instanceof Error ? err.message : "Could not delete.");
      }
    },
    [refreshJobs]
  );

  // ---------- drag/drop ----------

  function startDrag(
    e: ReactDragEvent<HTMLElement>,
    payload: DragPayload
  ): void {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    // Most browsers also need a plain-text fallback or the drag image
    // is treated as a no-op.
    e.dataTransfer.setData("text/plain", payload.id);
  }

  function readPayload(e: ReactDragEvent<HTMLElement>): DragPayload | null {
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as DragPayload;
    } catch {
      return null;
    }
  }

  // ---------- empty + loading states ----------

  const loading = jobs === null;
  const isEmpty =
    !loading && childFolders.length === 0 && childJobs.length === 0;
  const isFilteredEmpty =
    !loading && !isEmpty && filteredFolders.length === 0 && filteredJobs.length === 0;

  // ---------- render ----------

  return (
    <div className="files-shell">
      {/* Header stays full-width so the page identity + primary
          actions (New folder) sit at the top, the way every other
          authenticated page does. The toolbar + content below move
          into the shared 2-col layout so wide monitors render the
          same constrained reading column the user learned on
          Dashboard, instead of letting the grid stretch full-bleed
          to 1440px. */}
      <header className="files-header">
        <div className="files-header-left">
          <h1 className="files-title">Files</h1>
          {(() => {
            // Inside a folder the breadcrumbs already carry the name +
            // path, so use the subtitle for something more useful: a
            // quick count of what's in here. When the folder is empty
            // we suppress the subtitle entirely — the empty-state
            // panel below already shouts "This folder is empty" with
            // a big icon, and saying it twice in a row was redundant.
            if (!isRoot) {
              if (!current) return null;
              const counts = folderItemCount(current.id);
              if (counts.total === 0) return null;
              return <p className="files-subtitle">{folderContentLabel(counts)}</p>;
            }
            /* The long onboarding sentence used to live here ("organize
               them into folders, drag to move, click in to open") — it
               became ambient noise the second a user had any items.
               The mechanics are self-evident from the cards themselves;
               the empty-state copy below carries the first-run hint. */
            return <p className="files-subtitle">All of your audits in one place.</p>;
          })()}
        </div>
        <div className="files-header-actions">
          {/* No page-level "New audit" CTA — the topbar carries that
              globally on every authenticated page, and a second copy
              ~100px below it just creates two pills competing for the
              same click without giving the user any new affordance.
              "New folder" stays here because folder creation is
              page-specific and lives nowhere else. */}
          <button
            type="button"
            className="files-action-secondary"
            onClick={() => setCreateFolderOpen(true)}
          >
            <FolderPlusIcon /> New folder
          </button>
        </div>
      </header>

      {/* Full-width single column — Files is content-dense (grids and
          list rows), so it uses the whole page instead of the 70/30
          dashboard split; the old aside only held a drag tip. */}
      <div className="app-main">
      {/* ─── Toolbar: search, filters, sort, view toggle ─── */}
      <div className="files-toolbar">
        <div className="files-toolbar-left">
          <div className="files-search-wrap">
            <SearchIcon />
            <input
              type="text"
              className="files-search"
              placeholder="Search files and folders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search ? (
              <button
                type="button"
                className="files-search-clear"
                aria-label="Clear search"
                onClick={() => setSearch("")}
              >
                ×
              </button>
            ) : null}
          </div>
          <div className="files-status-chips">
            <button
              type="button"
              className={`files-chip${statusFilter === "all" ? " active" : ""}`}
              onClick={() => setStatusFilter("all")}
            >
              All
            </button>
            <button
              type="button"
              className={`files-chip${statusFilter === "completed" ? " active" : ""}`}
              onClick={() => setStatusFilter("completed")}
              disabled={completedCount === 0}
              aria-label="Show completed audits only"
              data-tip="Show completed audits only"
            >
              {/* Leading dot gives the eye a hook — a row of identical
                  chips reads as a wall otherwise. Color matches the
                  per-card status badge so the vocabulary lines up. */}
              <span className="files-chip-dot files-chip-dot-done" aria-hidden="true" />
              Completed{completedCount > 0 ? <span className="files-chip-count">({completedCount})</span> : null}
            </button>
            <button
              type="button"
              className={`files-chip${statusFilter === "failed" ? " active" : ""}`}
              onClick={() => setStatusFilter("failed")}
              disabled={failedCount === 0}
              aria-label="Show failed audits only"
              data-tip="Show failed audits only"
            >
              <span className="files-chip-dot files-chip-dot-failed" aria-hidden="true" />
              Failed{failedCount > 0 ? <span className="files-chip-count">({failedCount})</span> : null}
            </button>
            <button
              type="button"
              className={`files-chip${statusFilter === "capture" ? " active" : ""}`}
              onClick={() => setStatusFilter("capture")}
              disabled={captureCount === 0}
              aria-label="Show audits that couldn't be captured"
              data-tip="Finished, but every page came back blank — credits refunded"
            >
              <span className="files-chip-dot files-chip-dot-capture" aria-hidden="true" />
              Couldn&apos;t capture{captureCount > 0 ? <span className="files-chip-count">({captureCount})</span> : null}
            </button>
          </div>
        </div>
        <div className="files-toolbar-right">
          {presentTypes.size >= 2 ? (
            /* Same quiet dropdown pattern as Sort — a chip group here
               would add a third row of pills to an already busy
               toolbar. Labels match jobModeLabel so the filter and the
               cards speak the same vocabulary. */
            <div className="files-sort-wrap">
              <label htmlFor="files-type" className="files-sort-label">Type</label>
              <select
                id="files-type"
                className="files-sort-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
              >
                <option value="all">All types</option>
                {presentTypes.has("audit") ? <option value="audit">UX audits</option> : null}
                {presentTypes.has("snapshot") ? <option value="snapshot">Screenshots only</option> : null}
                {presentTypes.has("imageAudit") ? <option value="imageAudit">Image audits</option> : null}
              </select>
            </div>
          ) : null}
          <div className="files-sort-wrap">
            <label htmlFor="files-sort" className="files-sort-label">Sort by</label>
            <select
              id="files-sort"
              className="files-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
            >
              <option value="recent">Most recent</option>
              <option value="name">Name</option>
              <option value="modified">Last modified</option>
            </select>
          </div>
          <div className="files-view-toggle" role="radiogroup" aria-label="View mode">
            <button
              type="button"
              className={`files-view-btn${viewMode === "list" ? " active" : ""}`}
              onClick={() => setViewMode("list")}
              aria-pressed={viewMode === "list"}
              aria-label="Show as list"
              data-tip="Show as list"
            >
              <ListIcon />
            </button>
            <button
              type="button"
              className={`files-view-btn${viewMode === "grid" ? " active" : ""}`}
              onClick={() => setViewMode("grid")}
              aria-pressed={viewMode === "grid"}
              aria-label="Show as grid"
              data-tip="Show as grid"
            >
              <GridIcon />
            </button>
          </div>
        </div>
      </div>

      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          breadcrumbs={breadcrumbs}
          dragOverFolderId={dragOverFolderId}
          onDragOver={(targetId) => setDragOverFolderId(targetId)}
          onDrop={(targetId, e) => {
            e.preventDefault();
            const p = readPayload(e);
            setDragOverFolderId("IDLE");
            if (!p) return;
            if (p.kind === "folder" && p.id === targetId) return;
            void handleMove(p.kind, p.id, targetId);
          }}
        />
      )}

      {error ? (
        <div className="files-banner files-banner-error">
          <span>{error}</span>
          <button type="button" onClick={() => setError("")}>
            Dismiss
          </button>
        </div>
      ) : null}

      {loading ? (
        <FilesSkeleton />
      ) : isEmpty ? (
        <EmptyState isRoot={isRoot} onNewFolder={() => setCreateFolderOpen(true)} />
      ) : isFilteredEmpty ? (
        <div className="files-empty files-empty-filtered">
          <SearchEmptyIcon />
          <h2>No results</h2>
          <p>
            {search
              ? `Nothing matches "${search}". Try a different search term.`
              : "No audits match the current filter. Try changing or clearing your filters."}
          </p>
          <button
            type="button"
            className="files-action-secondary"
            onClick={() => { setSearch(""); setStatusFilter("all"); setTypeFilter("all"); }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="files-content">
          {filteredFolders.length > 0 ? (
            <section className="files-section">
              <h2 className="files-section-title">
                Folders
                <span className="files-section-count">({filteredFolders.length})</span>
              </h2>
              <ul className={viewMode === "grid" ? "files-grid" : "files-list"}>
                {filteredFolders.map((f) => {
                  const counts = folderItemCount(f.id);
                  return (
                  <FolderCard
                    key={f.id}
                    folder={f}
                    counts={counts}
                    viewMode={viewMode}
                    isDropTarget={dragOverFolderId === f.id}
                    isMoving={movingIds.has(f.id)}
                    onOpen={() => router.push(`/projects/${encodeURIComponent(f.id)}`)}
                    onRename={() =>
                      setEditing({ kind: "folder", id: f.id, name: f.name })
                    }
                    onMove={() =>
                      setMoveTarget({ kind: "folder", id: f.id, name: f.name })
                    }
                    onDelete={() =>
                      setConfirm({
                        kind: "folder",
                        id: f.id,
                        name: f.name,
                        childCount: counts.total,
                      })
                    }
                    onDragStart={(e) =>
                      startDrag(e, { kind: "folder", id: f.id })
                    }
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      setDragOverFolderId(f.id);
                    }}
                    onDragLeave={() => {
                      // Reset to IDLE — null would now incorrectly
                      // light up the root breadcrumb as a drop target.
                      if (dragOverFolderId === f.id) setDragOverFolderId("IDLE");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const p = readPayload(e);
                      setDragOverFolderId("IDLE");
                      if (!p) return;
                      if (p.kind === "folder" && p.id === f.id) return;
                      void handleMove(p.kind, p.id, f.id);
                    }}
                  />
                  );
                })}
              </ul>
            </section>
          ) : null}

          {filteredJobs.length > 0 ? (
            <section className="files-section">
              <h2 className="files-section-title">
                Audits
                <span className="files-section-count">({filteredJobs.length})</span>
              </h2>
              <ul className={viewMode === "grid" ? "files-audit-grid" : "files-list"}>
                {filteredJobs.map((j) => (
                  <JobCard
                    key={j.id}
                    job={j}
                    viewMode={viewMode}
                    isMoving={movingIds.has(j.id)}
                    onOpen={() => router.push(`/audit/${encodeURIComponent(j.id)}`)}
                    onRename={() =>
                      setEditing({
                        kind: "job",
                        id: j.id,
                        name: jobDisplayName(j),
                      })
                    }
                    onMove={() =>
                      setMoveTarget({
                        kind: "job",
                        id: j.id,
                        name: jobDisplayName(j),
                      })
                    }
                    onDelete={() =>
                      setConfirm({
                        kind: "job",
                        id: j.id,
                        name: jobDisplayName(j),
                      })
                    }
                    onDragStart={(e) => startDrag(e, { kind: "job", id: j.id })}
                  />
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
      </div>

      {createFolderOpen ? (
        <NameDialog
          title="New folder"
          confirmLabel="Create"
          placeholder="Folder name"
          onCancel={() => setCreateFolderOpen(false)}
          onConfirm={handleCreateFolder}
        />
      ) : null}

      {editing ? (
        <NameDialog
          title="Rename"
          confirmLabel="Save"
          initialValue={editing.name}
          placeholder="Name"
          onCancel={() => setEditing(null)}
          onConfirm={(name) => handleRename(editing.kind, editing.id, name)}
        />
      ) : null}

      {moveTarget ? (
        <MoveDialog
          target={moveTarget}
          folders={folders.filter((f) => !f.trashedAt)}
          onCancel={() => setMoveTarget(null)}
          onConfirm={async (newParentId) => {
            await handleMove(moveTarget.kind, moveTarget.id, newParentId);
            setMoveTarget(null);
          }}
        />
      ) : null}

      {confirm ? (
        <ConfirmDialog
          target={confirm}
          onCancel={() => setConfirm(null)}
          onConfirm={() => handleTrash(confirm.kind, confirm.id)}
        />
      ) : null}
    </div>
  );
}

// ============================================================
//   Breadcrumbs — drop targets too, so users can move into
//   any ancestor by dragging onto its breadcrumb segment.
// ============================================================

function Breadcrumbs({
  breadcrumbs,
  dragOverFolderId,
  onDragOver,
  onDrop,
}: {
  breadcrumbs: Folder[];
  // "IDLE" added so the root "Files" pill only lights up when an
  // active drag is over it — previously the default null state matched
  // the highlight condition and the root pill rendered as a permanent
  // drop target whenever the user was inside a folder.
  dragOverFolderId: string | null | "BREAD" | "IDLE";
  onDragOver: (targetId: string | null) => void;
  onDrop: (targetId: string | null, e: ReactDragEvent<HTMLElement>) => void;
}) {
  return (
    <nav className="files-breadcrumbs" aria-label="Folder path">
      {/* The old "LOCATION" all-caps prefix is gone — breadcrumbs are
          a recognized navigation pattern. Adding a shouty label on top
          made the whole thing feel like a wrapped widget rather than
          a quiet inline hint. */}
      <Link
        href="/projects"
        className={`files-breadcrumb${dragOverFolderId === null && breadcrumbs.length > 0 ? " is-drop" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          onDragOver(null);
        }}
        onDragLeave={() => onDragOver("BREAD")}
        onDrop={(e) => onDrop(null, e)}
      >
        Files
      </Link>
      {breadcrumbs.map((f, i) => (
        <span key={f.id} className="files-breadcrumb-row">
          <BreadcrumbSep />
          {i < breadcrumbs.length - 1 ? (
            <Link
              href={`/projects/${encodeURIComponent(f.id)}`}
              className={`files-breadcrumb${dragOverFolderId === f.id ? " is-drop" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                onDragOver(f.id);
              }}
              onDragLeave={() => onDragOver("BREAD")}
              onDrop={(e) => onDrop(f.id, e)}
            >
              {f.name}
            </Link>
          ) : (
            <span className="files-breadcrumb files-breadcrumb-current" aria-current="page">
              {f.name}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** Chevron separator — replaces the forward slash, which read as
 *  URL-ish and was harder for non-technical users to parse as "inside
 *  of". A right-pointing chevron is the conventional drill-down hint
 *  in file managers + design systems (Finder, Linear, Figma, etc.).
 */
function BreadcrumbSep() {
  return (
    <span className="files-breadcrumb-sep" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 6 15 12 9 18" />
      </svg>
    </span>
  );
}

// ============================================================
//   Folder card
// ============================================================

function FolderCard({
  folder,
  counts,
  viewMode,
  isDropTarget,
  isMoving,
  onOpen,
  onRename,
  onMove,
  onDelete,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  folder: Folder;
  counts: { subfolders: number; audits: number; total: number };
  viewMode: ViewMode;
  isDropTarget: boolean;
  isMoving: boolean;
  onOpen: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
  onDragStart: (e: ReactDragEvent<HTMLElement>) => void;
  onDragOver: (e: ReactDragEvent<HTMLElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: ReactDragEvent<HTMLElement>) => void;
}) {
  const dateStr = fmtDate(folder.updatedAt);
  return (
    <li
      className={
        `files-folder-card${isDropTarget ? " is-drop-target" : ""}` +
        `${isMoving ? " is-moving" : ""}` +
        `${viewMode === "list" ? " files-folder-card-list" : ""}`
      }
      draggable={!isMoving}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      aria-busy={isMoving || undefined}
    >
      <button
        type="button"
        className="files-folder-body"
        onClick={onOpen}
        onDoubleClick={onOpen}
        aria-label={`Open folder ${folder.name}`}
      >
        <span className="files-folder-icon" aria-hidden="true">
          <FolderIcon />
        </span>
        <span className="files-folder-meta">
          <span className="files-folder-name">{folder.name}</span>
          <span className="files-folder-sub">
            {folderContentLabel(counts)}
          </span>
        </span>
        {viewMode === "list" ? (
          <span className="files-row-date">{dateStr}</span>
        ) : null}
      </button>
      {isMoving ? <BusyOverlay label="Moving…" /> : null}
      <KebabMenu
        ariaLabel={`More options for folder ${folder.name}`}
        items={[
          { label: "Open", onClick: onOpen, icon: <OpenIcon /> },
          { label: "Rename", onClick: onRename, icon: <EditIcon /> },
          { label: "Move to…", onClick: onMove, icon: <MoveIcon /> },
          {
            // "Move to trash" matches the same action on individual
            // jobs — both are soft-deletes the user can restore later,
            // and saying "Delete folder" implied permanence the
            // behaviour doesn't actually have. Consistent verb across
            // both surfaces makes the destructiveness predictable.
            label: "Move to trash",
            onClick: onDelete,
            icon: <TrashIcon />,
            danger: true,
          },
        ]}
      />
    </li>
  );
}

// ============================================================
//   Audit (job) row
// ============================================================

function jobDisplayName(j: JobSummary): string {
  if (j.displayName && j.displayName.trim().length > 0) return j.displayName;
  if (j.siteName) return j.siteName;
  try {
    return new URL(j.sourceUrl).hostname.replace(/^www\./, "");
  } catch {
    return j.sourceUrl;
  }
}

function jobUrlLabel(j: JobSummary): string {
  // Image-upload audits keep the blob URL as sourceUrl — its host is the
  // opaque Vercel-storage bucket id, so show a clean label instead.
  if (j.runMode === "imageAudit") return "Uploaded image";
  try {
    const u = new URL(j.sourceUrl);
    const host = u.hostname.replace(/^www\./, "");
    const path = u.pathname === "/" ? "" : u.pathname;
    return `${host}${path}`;
  } catch {
    return j.sourceUrl.replace(/^https?:\/\/(www\.)?/i, "");
  }
}

function jobModeLabel(mode?: JobSummary["runMode"]): string {
  // Vocabulary lines up with /audit/new presets and the in-product
  // mode toggle helper text. Earlier it said "Snapshot" here, which is
  // an internal mode name; users see "Screenshots only" everywhere
  // else.
  if (mode === "snapshot") return "Screenshots only";
  if (mode === "imageAudit") return "Image audit";
  return "UX audit";
}

/**
 * Folder-card subline. "4 items" is technically true but doesn't tell
 * you whether you're about to walk into 4 audits or 3 folders + 1
 * audit, which makes the cards feel uniform and unscannable. Split
 * the count by type and join with a thin separator so the breakdown
 * is the first thing the eye picks up.
 */
function folderContentLabel(counts: {
  subfolders: number;
  audits: number;
  total: number;
}): string {
  if (counts.total === 0) return "Empty";
  const parts: string[] = [];
  if (counts.audits) parts.push(`${counts.audits} ${counts.audits === 1 ? "audit" : "audits"}`);
  if (counts.subfolders) parts.push(`${counts.subfolders} ${counts.subfolders === 1 ? "folder" : "folders"}`);
  return parts.join(" · ");
}

function jobViewportLabel(viewports: JobSummary["viewports"]): string {
  if (viewports.includes("desktop") && viewports.includes("mobile")) return "Desktop + mobile";
  if (viewports[0] === "mobile") return "Mobile";
  return "Desktop";
}

function jobPagesLabel(job: JobSummary): string {
  if (!job.pageCount) return "No pages";
  return `${job.pageCount} ${job.pageCount === 1 ? "page" : "pages"}`;
}

function jobFindingsLabel(job: JobSummary): {
  text: string;
  tone: "good" | "warn" | "error" | "muted" | "neutral" | "capture";
  tooltip?: string;
} {
  if (job.status === "error")
    return {
      text: "Failed",
      tone: "error",
      tooltip: job.error || "The capture failed. Try running this audit again.",
    };
  // Distinct from Failed: completed but every page came back blank. Stays
  // openable (the card isn't marked failed), so the user can open it to
  // read the explanation and upload a screenshot. Credits were refunded.
  if (job.captureFailed)
    return {
      text: "Couldn't capture",
      tone: "capture",
      tooltip:
        "Every page came back blank, so the credits were refunded. Open it to upload a screenshot instead.",
    };
  if (job.status !== "done") return { text: "Pending", tone: "muted" };

  // Snapshot runs never produce findings by design. The previous
  // "No findings" copy in green made the row look like the site
  // was clean when really we just didn't analyze it. Render an em
  // dash (canonical N/A signal in data tables) with a tooltip
  // explaining the state — adding findings is optional, so we
  // intentionally don't push the user with a CTA-style label.
  if (job.runMode === "snapshot") {
    return {
      text: "—",
      tone: "neutral",
      tooltip: "Screenshots only — no audit was run. You can add findings manually.",
    };
  }

  // Findings are normal product output, not a warning condition —
  // amber was implying something was wrong about having results.
  // Reserve warn/error tones for actual problem states (failures,
  // truly missing data) and let a non-zero finding count read as
  // neutral information.
  const count = job.findingsCount;
  if (typeof count === "number") {
    if (count === 0) return { text: "No findings", tone: "good" };
    return { text: `${count} ${count === 1 ? "finding" : "findings"}`, tone: "muted" };
  }
  // Backwards-compat for API responses predating findingsCount: avoid
  // mislabeling the 0..100 score as a finding count.
  return { text: "View audit", tone: "muted" };
}

function JobCard({
  job,
  viewMode,
  isMoving,
  onOpen,
  onRename,
  onMove,
  onDelete,
  onDragStart,
}: {
  job: JobSummary;
  viewMode: ViewMode;
  isMoving: boolean;
  onOpen: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
  onDragStart: (e: ReactDragEvent<HTMLElement>) => void;
}) {
  const name = jobDisplayName(job);
  // createdDate was previously shown under the audit name in the
  // list view; dashboard parity dropped that sub line in favor of
  // a single Updated time column, so only updatedDate is needed.
  const updatedDate = fmtDate(job.updatedAt || job.createdAt);
  const sourceLabel = jobUrlLabel(job);
  const modeLabel = jobModeLabel(job.runMode);
  const viewportLabel = jobViewportLabel(job.viewports);
  const pagesLabel = jobPagesLabel(job);
  const findings = jobFindingsLabel(job);
  const statusLabel =
    job.status === "running" || job.status === "pending"
      ? "Running"
      : job.status === "error"
        ? "Failed"
        : job.status === "done"
          ? "Done"
          : "";
  const statusClass =
    job.status === "running" || job.status === "pending"
      ? "files-status-running"
      : job.status === "error"
        ? "files-status-failed"
        : "files-status-done";
  // Failed audits have no canvas to open — the body stops being a
  // click target and the card explains the failure instead.
  const failed = job.status === "error";
  return (
    <li
      className={`files-job-card${isMoving ? " is-moving" : ""}${viewMode === "list" ? " files-job-card-list" : ""}${job.status === "error" ? " files-job-card-failed" : ""}`}
      draggable={!isMoving}
      onDragStart={onDragStart}
      aria-busy={isMoving || undefined}
    >
      <button
        type="button"
        className={`files-job-body${failed ? " files-job-body-static" : ""}`}
        onClick={failed ? undefined : onOpen}
        onDoubleClick={failed ? undefined : onOpen}
        aria-disabled={failed || undefined}
        aria-label={
          failed
            ? `Audit ${name} failed${job.error ? `: ${job.error}` : ""}`
            : `Open audit ${name}, ${findings.text}, ${pagesLabel}, ${viewportLabel}`
        }
      >
        <span className="files-job-thumb" aria-hidden="true">
          {job.thumbnailUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={job.thumbnailUrl} alt="" />
          ) : (
            <FileIcon />
          )}
          {viewMode === "grid" && job.status !== "done" ? (
            /* Status pill only on in-flight / failed cards — finished
               cards keep a clean screenshot and carry their findings
               count in the card footer instead of overlay chips. */
            <span className="files-job-thumb-badges">
              <span className={`files-row-status ${statusClass}`}>{statusLabel}</span>
            </span>
          ) : null}
          {/* Status dot — only shown in list view (grid view uses the
              full pill at the bottom of the thumb). Matches the
              dashboard's recent-runs list where status is encoded
              as a small dot on the thumb instead of a dedicated
              column, so the Files list reads with the same
              hierarchy. */}
          {viewMode === "list" && job.status === "running" ? (
            <span className="files-row-status-dot files-row-dot-running" aria-label="Running" data-tip="Running" />
          ) : null}
          {viewMode === "list" && job.status === "error" ? (
            <span className="files-row-status-dot files-row-dot-error" aria-label="Failed" data-tip="Failed" />
          ) : null}
        </span>
        <span className="files-job-meta">
          <span className="files-job-name">{name}</span>
          <span className="files-job-url">{sourceLabel}</span>
          {viewMode === "grid" ? (
            /* Facts collapsed into a single inline meta line so the
               card stops feeling like a card-in-card stack of tiles.
               Pages · Viewport · Updated reads as one quick sentence;
               the previous boxed tiles had bigger labels than the
               numbers and pulled the eye away from the audit name. */
            <span className="files-job-meta-line" aria-label="Audit details">
              <span>{pagesLabel}</span>
              <span className="files-job-meta-sep" aria-hidden="true">·</span>
              <span>{viewportLabel}</span>
              <span className="files-job-meta-sep" aria-hidden="true">·</span>
              <span>Updated {updatedDate}</span>
            </span>
          ) : null}
          {viewMode === "grid" ? (
            failed ? (
              /* Failed audits can't be opened — surface WHY it failed in
                 the slot where the open cue used to be. */
              <span className="files-job-error" title={job.error || undefined}>
                {job.error || "The capture failed. Try running this audit again."}
              </span>
            ) : (
              <span className="files-job-foot">
                <span
                  className={`files-row-findings files-findings-${findings.tone}`}
                  title={findings.tooltip}
                >
                  {findings.text}
                </span>
                <span className="files-open-cue">
                  Open audit <OpenIcon />
                </span>
              </span>
            )
          ) : null}
        </span>
        {viewMode === "list" ? (
          /* Row schema aligned with the dashboard's recent-runs row
             (rr-row): type · config (viewport + pages) · findings ·
             relative time. The previous schema had a separate Status
             column + an absolute date + a duplicate sub line in the
             meta block, which made the same audit read differently
             here vs on the dashboard. */
          <>
            <span className="files-row-type">{modeLabel}</span>
            <span className="files-row-config">
              <span>{viewportLabel}</span>
              <span className="files-row-config-sep" aria-hidden="true" />
              <span>{pagesLabel}</span>
            </span>
            <span
              className={`files-row-findings files-findings-${findings.tone}`}
              title={findings.tooltip}
            >
              {findings.text}
            </span>
            <span className="files-row-time">{relativeTime(job.updatedAt || job.createdAt)}</span>
          </>
        ) : null}
      </button>
      {isMoving ? <BusyOverlay label="Moving…" /> : null}
      <KebabMenu
        ariaLabel={`More options for audit ${name}`}
        items={[
          // Failed audits have nothing to open — managing actions only.
          ...(failed ? [] : [{ label: "Open", onClick: onOpen, icon: <OpenIcon /> }]),
          { label: "Rename", onClick: onRename, icon: <EditIcon /> },
          { label: "Move to…", onClick: onMove, icon: <MoveIcon /> },
          {
            label: "Move to trash",
            onClick: onDelete,
            icon: <TrashIcon />,
            danger: true,
          },
        ]}
      />
    </li>
  );
}

// ============================================================
//   Kebab menu
// ============================================================

function KebabMenu({
  items,
  /* Context-specific label so screen reader users hear "More options for
     Skyscanner" instead of a generic "More actions" — the previous
     fixed label gave no clue which row's actions were being opened
     when several kebabs were in the same list. Falls back to the
     generic label if no context is provided. */
  ariaLabel = "More options",
}: {
  items: Array<{ label: string; onClick: () => void; icon: React.ReactNode; danger?: boolean }>;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onClickOutside);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClickOutside);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="files-kebab" ref={ref}>
      <button
        type="button"
        className="files-kebab-btn"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <DotsIcon />
      </button>
      {open ? (
        <div className="files-kebab-menu" role="menu">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`files-kebab-item${item.danger ? " is-danger" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                item.onClick();
              }}
            >
              <span className="files-kebab-item-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ============================================================
//   Dialogs
// ============================================================

function NameDialog({
  title,
  confirmLabel,
  placeholder,
  initialValue = "",
  onCancel,
  onConfirm,
}: {
  title: string;
  confirmLabel: string;
  placeholder: string;
  initialValue?: string;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [value, setValue] = useState(initialValue);
  const trimmed = value.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 80;

  return (
    <DialogShell title={title} onCancel={onCancel}>
      <form
        className="files-dialog-body"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          onConfirm(trimmed);
        }}
      >
        <input
          type="text"
          value={value}
          autoFocus
          maxLength={80}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          className="files-dialog-input"
        />
        <DialogFooter
          onCancel={onCancel}
          confirmLabel={confirmLabel}
          confirmDisabled={!valid}
        />
      </form>
    </DialogShell>
  );
}

function MoveDialog({
  target,
  folders,
  onCancel,
  onConfirm,
}: {
  target: MoveTarget;
  folders: Folder[];
  onCancel: () => void;
  onConfirm: (newParentId: string | null) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  // For folder targets, exclude the target itself + its descendants from
  // valid destinations (you can't move a folder into itself).
  const blocked = useMemo(() => {
    const out = new Set<string>();
    if (target.kind !== "folder") return out;
    out.add(target.id);
    const queue = [target.id];
    while (queue.length) {
      const cur = queue.shift()!;
      for (const f of folders) {
        if (f.parentId === cur && !out.has(f.id)) {
          out.add(f.id);
          queue.push(f.id);
        }
      }
    }
    return out;
  }, [target, folders]);

  // Render as a flat indented list — simpler than a true tree picker
  // and renders fast for hundreds of folders.
  const flat = useMemo(() => {
    const byParent = new Map<string | null, Folder[]>();
    for (const f of folders) {
      const arr = byParent.get(f.parentId) ?? [];
      arr.push(f);
      byParent.set(f.parentId, arr);
    }
    for (const arr of byParent.values()) arr.sort((a, b) => a.name.localeCompare(b.name));
    const out: Array<{ folder: Folder; depth: number }> = [];
    function walk(parent: string | null, depth: number) {
      const kids = byParent.get(parent) ?? [];
      for (const f of kids) {
        out.push({ folder: f, depth });
        walk(f.id, depth + 1);
      }
    }
    walk(null, 0);
    return out;
  }, [folders]);

  return (
    <DialogShell title={`Move "${target.name}"`} onCancel={onCancel}>
      <div className="files-dialog-body">
        <p className="files-dialog-sub">Pick a destination folder.</p>
        <div className="files-move-list" role="listbox">
          <button
            type="button"
            role="option"
            aria-selected={selected === null}
            className={`files-move-row${selected === null ? " is-selected" : ""}`}
            onClick={() => setSelected(null)}
          >
            <HomeIcon /> Files
            <span className="files-move-tag">Root</span>
          </button>
          {flat.map(({ folder, depth }) => {
            const isBlocked = blocked.has(folder.id);
            return (
              <button
                key={folder.id}
                type="button"
                role="option"
                aria-selected={selected === folder.id}
                disabled={isBlocked}
                className={`files-move-row${selected === folder.id ? " is-selected" : ""}${isBlocked ? " is-blocked" : ""}`}
                onClick={() => setSelected(folder.id)}
                style={{ paddingLeft: 16 + depth * 16 }}
              >
                <FolderIcon /> {folder.name}
                {isBlocked ? (
                  <span className="files-move-tag">Can&apos;t move here</span>
                ) : null}
              </button>
            );
          })}
        </div>
        <DialogFooter
          onCancel={onCancel}
          confirmLabel="Move"
          onConfirm={() => onConfirm(selected)}
        />
      </div>
    </DialogShell>
  );
}

function ConfirmDialog({
  target,
  onCancel,
  onConfirm,
}: {
  target: ConfirmTarget;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isFolder = target.kind === "folder";
  const isFolderWithChildren = isFolder && target.childCount > 0;
  // Title + body + button all say the same thing now: this is a
  // soft-delete to trash, not a hard delete. The old title "Delete
  // folder?" implied permanence while the body promised "you can
  // restore it later", which is the kind of mismatch that makes
  // people hesitate.
  return (
    <DialogShell
      title={isFolder ? "Move folder to trash?" : "Move to trash?"}
      onCancel={onCancel}
    >
      <div className="files-dialog-body">
        <p className="files-dialog-sub">
          {isFolderWithChildren
            ? `"${target.name}" and ${target.childCount} ${
                target.childCount === 1 ? "item" : "items"
              } inside it will be moved to the trash. You can restore them later.`
            : isFolder
              ? `"${target.name}" will be moved to the trash. You can restore it later.`
              : `"${target.name}" will be moved to the trash. You can restore it later.`}
        </p>
        <DialogFooter
          onCancel={onCancel}
          confirmLabel="Move to trash"
          danger
          onConfirm={onConfirm}
        />
      </div>
    </DialogShell>
  );
}

function DialogShell({
  title,
  onCancel,
  children,
}: {
  title: string;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="files-dialog-backdrop" onClick={onCancel}>
      <div
        className="files-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="files-dialog-head">
          <h3>{title}</h3>
          <button
            type="button"
            className="files-dialog-close"
            onClick={onCancel}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function DialogFooter({
  onCancel,
  onConfirm,
  confirmLabel,
  confirmDisabled,
  danger,
}: {
  onCancel: () => void;
  onConfirm?: () => void;
  confirmLabel: string;
  confirmDisabled?: boolean;
  danger?: boolean;
}) {
  return (
    <footer className="files-dialog-foot">
      <button type="button" className="files-dialog-btn" onClick={onCancel}>
        Cancel
      </button>
      <button
        type={onConfirm ? "button" : "submit"}
        className={`files-dialog-btn files-dialog-btn-${danger ? "danger" : "primary"}`}
        disabled={confirmDisabled}
        onClick={onConfirm}
      >
        {confirmLabel}
      </button>
    </footer>
  );
}

// ============================================================
//   Empty + skeleton
// ============================================================

function FilesSkeleton() {
  // No "Loading" h2 — using a literal heading made the skeleton read
  // like a real section called "Loading" to screen readers and looked
  // like a misrender to sighted users. aria-busy on the container is
  // the right signal; the shimmering rows do the rest visually.
  return (
    <div className="files-content" aria-busy="true" aria-live="polite">
      <ul className="files-grid">
        {[0, 1, 2, 3].map((i) => (
          <li key={i} className="files-folder-card files-folder-card-sk">
            <span className="sk sk-circle" />
            <span className="sk sk-line" style={{ width: "60%" }} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyState({
  isRoot,
  onNewFolder,
}: {
  isRoot: boolean;
  onNewFolder: () => void;
}) {
  return (
    <div className="files-empty">
      <FolderEmptyIcon />
      <h2>{isRoot ? "Your library is empty" : "This folder is empty"}</h2>
      <p>
        {isRoot
          ? "Run an audit to get started, or create a folder to organize what comes next."
          : "Run a new audit and move it in, drag one in from another folder, or add subfolders for grouping."}
      </p>
      {/* Both root and subfolder empty states now expose New audit —
          the subfolder version used to only offer New folder, leaving
          the user with no way to fill the folder with what folders are
          for. */}
      <div className="files-empty-actions">
        <button type="button" className="files-action-secondary" onClick={onNewFolder}>
          <FolderPlusIcon /> New folder
        </button>
        <Link href="/audit/new" className="files-action-primary">
          <PlusIcon /> New audit
        </Link>
      </div>
    </div>
  );
}

// ============================================================
//   Helpers
// ============================================================

function fmtDate(ts: number | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

/** Relative time — matches the dashboard's date display so the
 *  audit rows here read the same way as the recent-runs list. */
function relativeTime(when: number | null | undefined): string {
  if (!when) return "";
  const diff = Date.now() - when;
  const sec = Math.round(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

// ============================================================
//   Icons
// ============================================================

// Translucent overlay that sits on top of a row while it's being
// moved so the user gets immediate "yes, I heard you" feedback rather
// than wondering whether the drag actually registered.
function BusyOverlay({ label }: { label: string }) {
  return (
    <div className="files-busy-overlay" aria-hidden="true">
      <span className="files-busy-spinner" />
      <span className="files-busy-label">{label}</span>
    </div>
  );
}

function FolderIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function FolderEmptyIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="files-empty-icon">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      <path d="M12 11v6" />
      <path d="M9 14h6" />
    </svg>
  );
}

function FolderPlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      <line x1="12" y1="11" x2="12" y2="17" />
      <line x1="9" y1="14" x2="15" y2="14" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function MoveIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 6 3 12 9 18" />
      <polyline points="15 18 21 12 15 6" />
      <line x1="3" y1="12" x2="21" y2="12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function OpenIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function SearchEmptyIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="files-empty-icon">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="8" y1="8" x2="14" y2="14" /><line x1="14" y1="8" x2="8" y2="14" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

// Pre-load the folder list as soon as ProjectsClient mounts so the
// sidebar's recent-folders panel has data even on a hard refresh.
void loadFolders();
